import { LineStyle } from 'lightweight-charts'
import { log as debugLog } from '@/utils/logger'
import { toUtcSeconds } from '@/utils/utcTime'
import { LabelOverlaySeries } from '@/indicators/ict_quantum/LabelOverlaySeries'
import { mtfSuffix, tfSeconds } from '@/utils/mtf'
import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'

// ─── Helpers de tiempo y validación ───────────────────────────────────────────

// Conversor ÚNICO compartido (@/utils/utcTime, v1.3.2): acepta segundos,
// milisegundos (payloads /charting/*), strings 'YYYY-MM-DD' (TFs diarios+) y
// business-day objects de LWC. Antes cleanTime solo convertía ms→s de números
// y los times string de 1D/1W/1M pasaban CRUDOS → comparaciones/Math.max
// número-vs-string en NaN o silencio (nada pintado en diario+). Contrato
// externo intacto: null para lo no interpretable, segundos UTC si no.
function cleanTime(t) {
  return toUtcSeconds(t)
}

function cleanValue(v) {
  if (v == null || Number.isNaN(v) || !Number.isFinite(v)) return null
  return v
}

function timeToSeconds(t) {
  return toUtcSeconds(t)
}

function getVisibleTimeRange(chart, series) {
  try {
    const timeScale = chart.timeScale()
    const logicalRange = timeScale.getVisibleLogicalRange()
    if (!logicalRange) return null
    const data = series && typeof series.data === 'function' ? series.data() : []
    if (!data.length) return null
    const fromIdx = Math.max(0, Math.floor(logicalRange.from))
    const toIdx = Math.min(data.length - 1, Math.ceil(logicalRange.to))
    if (fromIdx > toIdx || fromIdx >= data.length) return null
    return { from: timeToSeconds(data[fromIdx].time), to: timeToSeconds(data[toIdx].time) }
  } catch (e) {
    return null
  }
}

function overlapsVisibleRange(start, end, range) {
  if (!range) return true
  const s = timeToSeconds(start)
  const e = timeToSeconds(end)
  if (s == null || e == null) return false
  return s <= range.to && e >= range.from
}

function sortByTimeDesc(arr, key = 'time') {
  return [...arr].sort((a, b) => (cleanTime(b[key]) ?? 0) - (cleanTime(a[key]) ?? 0))
}

// ─── Datos externos (inyectados desde ChartPage / ChartPublicPage) ────────────

let externalData = null
let externalVersion = 0

export function setExternalData(data) {
  externalData = data
  externalVersion++
}

export function getExternalData() {
  return externalData
}

export function getExternalVersion() {
  return externalVersion
}

// ─── Configuración por defecto ──────────────────────────────────────────────────

export const defaultConfig = {
  _version: 3,

  // ── Lógica / Estructura ──────────────────────────────────────────────────
  structure: {
    showSwingStruct: true,
    showInternalStruct: true,
    showSwingLabels: true,
    swingLen: 10,
    internalLen: 5,
    minMoveATRMult: 0.5,
    maxHistoryEvents: 5,
    maxInternalEvents: 3,
  },

  // ── MTF: timeframes adicionales (solo mayores que el del chart, máx. 2) ──
  // Vacío = MTF OFF (default). La selección se sanea contra el TF del chart
  // en cada fetch/render; un TF que deja de ser mayor simplemente no se pide.
  htfTimeframes: [],

  // ── Estilo Quantum ───────────────────────────────────────────────────────
  // Hex exactos copiados de ict_quantum en producción.
  colorBull: '#1B5E20',
  colorBear: '#B71C1C',

  // ── Dashboard ────────────────────────────────────────────────────────────
  showDashboard: true,
  dashboardMinimized: false,
  badgePosition: { x: 8, y: 8 },
  panelSections: {},
}

// ─── detect() ─────────────────────────────────────────────────────────────────

export function detect(candles, config) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!externalData) {
    const t1 = typeof performance !== 'undefined' ? performance.now() : 0
    debugLog(`[choch_bos_quantum] detect empty: ${(t1 - t0).toFixed(2)}ms`)
    return null
  }
  const result = {
    ...externalData,
    _dataVersion: externalVersion,
  }
  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  debugLog(`[choch_bos_quantum] detect from external: ${(t1 - t0).toFixed(2)}ms`)
  return result
}

// ─── render() ─────────────────────────────────────────────────────────────────

export function render(chart, candleSeries, result, config, api) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!result || !result.lines) return
  const cfg = { ...defaultConfig, ...(config || {}) }
  const scfg = cfg.structure || {}

  const allCandles = candleSeries && candleSeries.data ? candleSeries.data() : []
  const lastCandle = allCandles.length > 0 ? allCandles[allCandles.length - 1] : null
  const lastTime = lastCandle ? lastCandle.time : null

  // Acumulador de etiquetas CHoCH/BOS pintadas en canvas (LabelOverlaySeries)
  // para evitar el desfase de DOM overlay al hacer pan/zoom.
  const labelsByTime = {}
  const addLabel = (opts) => {
    const t = opts?.time
    if (t == null || Number.isNaN(t)) return
    if (!labelsByTime[t]) labelsByTime[t] = []
    labelsByTime[t].push(opts)
  }

  let visibleRange = null
  try {
    visibleRange = getVisibleTimeRange(chart, candleSeries)
  } catch {}

  // Ventana de DATOS cargada — fija e independiente del viewport (misma regla
  // que Order Blocks: la detección corre server-side sobre esa ventana y el
  // viewport solo clipea visualmente). Las líneas se filtran contra ella, no
  // contra el rango visible: una línea pintada jamás aparece/desaparece al
  // hacer pan y los contadores del badge son estables (badge == pintadas).
  const firstCandleTime = allCandles.length > 0 ? cleanTime(allCandles[0].time) : null
  const lastCandleTime = allCandles.length > 0 ? cleanTime(allCandles[allCandles.length - 1].time) : null
  const dataWindow = firstCandleTime != null && lastCandleTime != null
    ? { from: firstCandleTime, to: lastCandleTime }
    : null

  const candidateLines = []
  for (const line of result.lines || []) {
    const start = cleanTime(line.start_time)
    const end = cleanTime(line.end_time)
    const price = cleanValue(line.price)
    if (start == null || end == null || price == null) continue
    if (!overlapsVisibleRange(start, end, dataWindow)) continue

    // MTF: las líneas de timeframes adicionales vienen marcadas con `_mtf`
    // por el merge de la página (ver utils/mtf.js). Se distinguen por
    // etiqueta con sufijo de TF (`BOS · 4H`) y línea dotted — sólido = TF
    // propio, dotted = HTF (no hay más texturas en este indicador).
    const mtf = line._mtf && tfSeconds(line._mtf) != null ? line._mtf : null

    const type = line.type
    let visible = false
    let category = 'other'
    let maxEvents = Infinity

    if (type === 'BOS' || type === 'CHoCH') {
      if (scfg.showSwingStruct === false) continue
      visible = true
      category = 'swing'
      maxEvents = scfg.maxHistoryEvents ?? 5
    } else if (type === 'INTERNAL_BOS' || type === 'INTERNAL_CHoCH') {
      if (scfg.showInternalStruct === false) continue
      visible = true
      category = 'internal'
      maxEvents = scfg.maxInternalEvents ?? 3
    }

    if (!visible) continue
    const direction = line.direction === 'bull' ? 'bull' : 'bear'
    const color = direction === 'bull' ? cfg.colorBull : cfg.colorBear
    candidateLines.push({
      start, end, price, color, direction,
      type, category, maxEvents, mtf,
      label: line.label, showLabel: line.show_label,
    })
  }

  // Limitar a los N eventos más recientes de cada categoría (ICT canon).
  const sortedLines = sortByTimeDesc(candidateLines, 'start')
  const limitedLines = []
  const counts = { swing: 0, internal: 0, other: 0 }
  for (const line of sortedLines) {
    const cat = line.category || 'other'
    if (counts[cat] >= line.maxEvents) continue
    counts[cat]++
    limitedLines.push(line)
  }

  // Reordenar cronológicamente para dibujar líneas de izquierda a derecha.
  const orderedLines = [...limitedLines].sort((a, b) => a.start - b.start)

  // Dedup por (inicio, fin, nivel, dirección) — regla D.2: nunca más
  // etiquetas que líneas visibles. Swing gana sobre interna en el backend;
  // aquí evitamos cualquier duplicado residual. MTF: propio y HTF no deberían
  // solapar por construcción (sus tiempos vienen de rejillas de velas
  // distintas); si solapan, manda el PROPIO — se registran primero sus claves.
  const lineKey = (line) => `${line.start}|${line.end}|${line.price}|${line.direction}`
  const seenLineKeys = new Set()
  const keptLines = new Set()
  for (const line of orderedLines) {
    if (line.mtf) continue
    const key = lineKey(line)
    if (seenLineKeys.has(key)) continue
    seenLineKeys.add(key)
    keptLines.add(line)
  }
  for (const line of orderedLines) {
    if (!line.mtf) continue
    const key = lineKey(line)
    if (seenLineKeys.has(key)) continue
    seenLineKeys.add(key)
    keptLines.add(line)
  }
  const dedupedLines = orderedLines.filter((line) => keptLines.has(line))

  const activeEndTime = lastTime != null ? timeToSeconds(lastTime) : null

  // Instrumentación para verificación programática de huérfanas (Standard G.8).
  const renderedLines = []
  const renderedLabels = []

  for (const line of dedupedLines) {
    // Líneas nunca full-width: si el backend ha extendido el final más allá del
    // presente, lo truncamos al último dato visible.
    let end = line.end
    if (activeEndTime != null && end > activeEndTime) {
      end = activeEndTime
    }

    // Línea de estructura: 1 px, colores Quantum bull/bear. Sólida en el TF
    // propio; dotted si viene de un timeframe adicional (marca MTF canónica).
    const ls = api.addSeries(c => c.addLineSeries({
      title: '',
      color: line.color,
      lineWidth: 1,
      lineStyle: line.mtf ? LineStyle.Dotted : LineStyle.Solid,
      lastValueVisible: false,
      priceLineVisible: false,
      autoscaleInfoProvider: () => null,
    }))
    if (ls) ls.setData([
      { time: line.start, value: line.price },
      { time: end, value: line.price },
    ])
    renderedLines.push({ start: line.start, end, price: line.price, type: line.type, direction: line.direction, mtf: line.mtf })

    // Etiqueta de texto plano en canvas, estilo ICT/SMC Quantum.
    // Centrada en el punto medio de la línea visible y siempre por encima de
    // ella (espacio vacío, nunca dentro de una vela). Texto tiny regular, sin
    // fondo, sin pill, sin roundRect, sin borde, sin puntos de ancla.
    // Se ancla directamente al tiempo del punto medio (no se usa xOffsetBars:
    // LabelOverlaySeries calcula barWidth entre etiquetas dispersas, lo que
    // desplazaba la etiqueta lejos de su línea).
    if (line.showLabel && line.label && scfg.showSwingLabels !== false) {
      const labelText = line.mtf ? line.label + mtfSuffix(line.mtf) : line.label
      let midTime
      if (visibleRange) {
        // Punto medio de la porción visible de la línea: toda línea visible
        // tiene su etiqueta visible cerca de su centro.
        const visibleStart = Math.max(line.start, visibleRange.from)
        const visibleEnd = Math.min(end, visibleRange.to)
        midTime = (visibleStart + visibleEnd) / 2
      } else {
        midTime = (line.start + end) / 2
      }
      addLabel({
        time: midTime,
        price: line.price,
        text: labelText,
        color: line.color,
        textColor: line.color,
        anchor: 'above',
        offset: 10,
        priority: line.type === 'BOS' || line.type === 'CHoCH' ? 10 : 7,
        plaqueStyle: 'plain',
      })
      renderedLabels.push({ time: midTime, price: line.price, text: labelText, lineStart: line.start, lineEnd: end })
    }
  }

  // Contadores de lo REALMENTE pintado — única fuente del badge, para que
  // badge y canvas cuadren 1:1 en cualquier config (toggles, límites…).
  // Los contadores clásicos solo cuentan el TF PROPIO; las líneas HTF van
  // aparte, por TF, para la línea "HTF: 4H ×2 · 1D ×1" del badge.
  const ownLines = renderedLines.filter((l) => !l.mtf)
  const htfCounts = {}
  for (const l of renderedLines) {
    if (l.mtf) htfCounts[l.mtf] = (htfCounts[l.mtf] || 0) + 1
  }
  result._paintedCounts = {
    swing: ownLines.filter((l) => l.type === 'BOS' || l.type === 'CHoCH').length,
    internal: ownLines.filter((l) => l.type === 'INTERNAL_BOS' || l.type === 'INTERNAL_CHoCH').length,
    total: ownLines.length,
    htf: htfCounts,
  }

  // Etiquetas CHoCH/BOS pintadas en canvas, sincronizadas frame a frame.
  if (Object.keys(labelsByTime).length > 0) {
    const labelSeries = api.addCustomSeries(new LabelOverlaySeries(), {
      color: 'transparent',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      autoscaleInfoProvider: () => null,
      priceScaleId: 'right',
      plaqueStyle: 'plain',
      font: '10px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif',
    })
    if (labelSeries) {
      const labelData = Object.keys(labelsByTime)
        .map(t => ({ time: Number(t), labels: labelsByTime[t] }))
        .sort((a, b) => a.time - b.time)
      labelSeries.setData(labelData)
    }
  }

  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  debugLog(`[choch_bos_quantum] render done: ${(t1 - t0).toFixed(2)}ms`)

  // Exponer datos de render para verificación programática externa (Playwright).
  if (typeof window !== 'undefined') {
    window.__chochBosRender = {
      lines: renderedLines,
      labels: renderedLabels,
      counts: result._paintedCounts,
      timestamp: Date.now(),
    }
  }
}

export default {
  id: 'choch_bos_quantum',
  name: 'CHoCH / BOS Quantum',
  version: '1.1.0',
  // Nada de este render depende del viewport para el filtrado (ventana de
  // datos cargada, como Order Blocks): las primitivas son nativas del chart y
  // se desplazan con el pan. Opt-out del repintado por panTick para que las
  // líneas no parpadeen al arrastrar (anti-flicker, regla OB).
  renderOnPan: false,
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
}
